import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Binoculars, ClipboardList, Search, Target } from 'lucide-react';
import { PlayerAvatar } from '@/components/PlayerAvatar';
import { ProspectProgressBar } from '@/components/ProspectProgressBar';
import {
  useLogProspectTouchBatch,
  usePatchProspectProfile,
  useProspects,
  type ProspectProfile,
  type ProspectTouch,
  type SkippedTouch,
  type TouchTracker,
} from '@/hooks/useProspects';
import { ApiError } from '@/lib/api';
import {
  MAX_PROSPECT_BATCH_TOUCHES,
  localTouchDate,
  PROSPECT_TOUCH_TYPES,
  prospectProgress,
  prospectTouchPoints,
  type ProspectPace,
  type ProspectTouchType,
} from '../../shared/prospect-engagement';

const SHORT_TOUCH_LABELS: Record<ProspectTouchType, string> = {
  in_person_viewing: 'View',
  in_person_meeting: 'Meet',
  video_coaching: 'Coach',
  check_in_call: 'Call',
  watched_shifts_online: 'Watch',
  postgame_text: 'Text',
  email: 'Email',
};

const TRACKER_COPY: Record<TouchTracker, { eyebrow: string; title: string; metric: string; noun: string }> = {
  prospect: { eyebrow: 'Prospect Touch Tracker', title: 'Prospects', metric: 'Prospects', noun: 'prospect' },
  client: { eyebrow: 'Client Touch Tracker', title: 'Clients', metric: 'Clients', noun: 'client' },
};

// The Prospects page and the Clients page: same tracker, points and touch types.
export function Prospects({ tracker = 'prospect' }: { tracker?: TouchTracker }) {
  const copy = TRACKER_COPY[tracker];
  const prospects = useProspects(tracker);
  const data = prospects.data;
  const [search, setSearch] = useState('');
  const touchedProspects = new Set(
    (data?.touches ?? []).map((touch) => touch.prospectId).filter(Boolean)
  );
  const untouched = (data?.profiles ?? []).filter(
    (profile) => !touchedProspects.has(profile.id)
  ).length;

  return (
    <section className="space-y-4 sm:space-y-5">
      <div className="surface overflow-hidden rounded-control">
        <div className="flex flex-col gap-4 border-b border-border/80 p-4 sm:p-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-bartlettGold">
              {copy.eyebrow}
            </p>
            <h1 className="mt-1.5 text-2xl font-semibold tracking-normal">{copy.title}</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted">
              Track every player touch against the P0–P3 point goals. Matching searches move to the
              top without hiding the rest of the list. Bars run orange to green as a player nears
              his goal; the thin tick marks where today&apos;s pace says he should be.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-3 lg:min-w-[420px]">
            <Metric icon={Target} label={copy.metric} value={String(data?.profiles.length ?? '—')} />
            <Metric
              icon={ClipboardList}
              label="Logged touches"
              value={String(data?.touches.length ?? '—')}
            />
            <Metric icon={Binoculars} label="Need touch" value={String(data ? untouched : '—')} />
          </div>
        </div>
      </div>

      <div className="surface-soft rounded-control p-3 sm:p-4">
        <label className="relative block min-w-0">
          <Search
            size={16}
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            type="search"
            placeholder="Search player name — matches move to the top"
            aria-label="Search player name"
            className="focus-ring h-10 w-full rounded-control border border-border bg-white pl-9 pr-3 text-sm outline-none focus:border-bartlett"
          />
        </label>
      </div>

      {prospects.isLoading && <SkeletonGrid />}
      {prospects.isError && (
        <div className="rounded-control border border-flag-critical/30 bg-flag-critical/5 p-4 text-sm text-flag-critical">
          Couldn&apos;t load {copy.noun} data.
        </div>
      )}
      {data && <TouchTracker profiles={data.profiles} touches={data.touches} search={search} noun={copy.noun} />}
    </section>
  );
}

function TouchTracker({
  profiles,
  touches,
  search,
  noun,
}: {
  profiles: ProspectProfile[];
  touches: ProspectTouch[];
  search: string;
  noun: string;
}) {
  const batch = useLogProspectTouchBatch();
  const [selections, setSelections] = useState<Record<string, ProspectTouchType[]>>({});
  // Scott often logs a night's touches later, so the batch date is his choice;
  // it starts on today and cannot be in the future.
  const today = localTouchDate();
  const [touchDate, setTouchDate] = useState(today);
  const batchDate = touchDate && touchDate <= today ? touchDate : today;
  const rows = useMemo(
    () => orderTrackingRows(buildTrackingRows(profiles, touches), search),
    [profiles, search, touches]
  );
  const paceGroups = {
    ahead: rows.filter((row) => row.pace === 'ahead'),
    on_track: rows.filter((row) => row.pace === 'on_track'),
    behind: rows.filter((row) => row.pace === 'behind'),
  } satisfies Record<ProspectPace, ProspectTrackingRow[]>;
  const selectedRows = Object.entries(selections).filter(([, entries]) => entries.length > 0);
  const selectedTouches = selectedRows.reduce((sum, [, entries]) => sum + entries.length, 0);
  const selectedPoints = selectedRows.reduce(
    (sum, [, entries]) =>
      sum + entries.reduce((rowSum, type) => rowSum + prospectTouchPoints(type), 0),
    0
  );

  function toggleTouch(prospectId: string, type: ProspectTouchType) {
    setSelections((current) => {
      const selected = current[prospectId] ?? [];
      if (!selected.includes(type) && Object.values(current).reduce((sum, entries) => sum + entries.length, 0) >= MAX_PROSPECT_BATCH_TOUCHES) return current;
      const next = selected.includes(type)
        ? selected.filter((item) => item !== type)
        : [...selected, type];
      if (next.length === 0) {
        const nextSelections = { ...current };
        delete nextSelections[prospectId];
        return nextSelections;
      }
      return { ...current, [prospectId]: next };
    });
    batch.reset();
  }

  function logBatch() {
    if (selectedTouches === 0 || selectedTouches > MAX_PROSPECT_BATCH_TOUCHES || batch.isPending) return;
    batch.mutate(
      {
        requestId: crypto.randomUUID(),
        rows: selectedRows.map(([prospectId, entries]) => ({
          prospectId,
          entries: entries.map((touchType) => ({ touchType, occurredAt: batchDate })),
        })),
      },
      { onSuccess: () => setSelections({}) }
    );
  }

  return (
    <div className="space-y-5">
      <section className="grid gap-3 lg:grid-cols-3">
        <PaceGroup title="Ahead of schedule" tone="good" rows={paceGroups.ahead} />
        <PaceGroup title="On track" tone="neutral" rows={paceGroups.on_track} />
        <PaceGroup title="Behind schedule" tone="warn" rows={paceGroups.behind} />
      </section>

      <section className="surface-soft overflow-hidden rounded-control">
        <div className="border-b border-border p-3 sm:px-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-base font-semibold">Touch progress</h2>
              <p className="mt-0.5 text-xs text-muted">
                Select up to {MAX_PROSPECT_BATCH_TOUCHES} touches per batch, then log them together.
              </p>
            </div>
            <div className="rounded-control border border-border bg-white px-3 py-2">
              <span className="font-mono text-[11px] text-muted" aria-live="polite">
                {selectedRows.length} player{selectedRows.length === 1 ? '' : 's'} ·{' '}
                {selectedTouches} touch{selectedTouches === 1 ? '' : 'es'} · +{selectedPoints} pts
              </span>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto bg-white">
          <div className="min-w-[1120px]">
            <div className="grid grid-cols-[130px_minmax(270px,1fr)_100px_minmax(510px,1.5fr)_52px] gap-3 border-b border-border bg-bg px-3 py-2 font-mono text-[10px] font-semibold uppercase tracking-wide text-muted">
              <span>Priority</span>
              <span>Player &amp; progress</span>
              <span>Latest</span>
              <span>Select touches</span>
              <span>Move</span>
            </div>
            <div className="divide-y divide-border">
              {rows.map((row) => (
                <TrackingRow
                  key={row.profile.id}
                  noun={noun}
                  row={row}
                  atLimit={selectedTouches >= MAX_PROSPECT_BATCH_TOUCHES || batch.isPending}
                  highlighted={matchesPlayerSearch(row.profile, search)}
                  selected={selections[row.profile.id] ?? []}
                  onToggle={(type) => toggleTouch(row.profile.id, type)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <aside
        aria-label="Master touch log"
        className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] left-4 right-4 z-50 flex items-center gap-2 rounded-control border border-white/15 bg-ink p-2.5 shadow-2xl sm:left-auto sm:right-5 sm:w-auto"
      >
        <div className="min-w-0 flex-1 px-1 text-white sm:min-w-[150px]">
          <div className="font-mono text-[10px] font-semibold uppercase tracking-wide text-bartlettGold">
            Master touch log
          </div>
          <div className="truncate font-mono text-[11px] text-white/75" aria-live="polite">
            {selectedRows.length} player{selectedRows.length === 1 ? '' : 's'} ·{' '}
            {selectedTouches} touch{selectedTouches === 1 ? '' : 'es'} · +{selectedPoints}
          </div>
          <label className="mt-1 flex items-center gap-1.5 font-mono text-[10px] text-white/70">
            <span>Date</span>
            <input
              type="date"
              value={batchDate}
              max={today}
              onChange={(event) => setTouchDate(event.target.value)}
              aria-label="Date of these touches"
              className="focus-ring h-6 rounded-[4px] border border-white/20 bg-white/10 px-1 font-mono text-[11px] text-white [color-scheme:dark]"
            />
            {batchDate !== today && (
              <button type="button" onClick={() => setTouchDate(today)} className="focus-ring underline">
                today
              </button>
            )}
          </label>
          {batch.isError && (
            <div className="mt-0.5 max-w-[22rem] text-[10px] text-red-300">{batchErrorMessage(batch.error)}</div>
          )}
          {batch.isSuccess && (
            <div className="mt-0.5 max-w-[22rem] text-[10px] text-green-300">
              Logged {batch.data.logged} touch{batch.data.logged === 1 ? '' : 'es'} for {batch.variables?.rows[0]?.entries[0]?.occurredAt ?? batchDate}.
              {(batch.data.skipped?.length ?? 0) > 0 && (
                <span className="text-bartlettGold"> {skippedMessage(batch.data.skipped!)}</span>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          disabled={selectedTouches === 0 || batch.isPending}
          onClick={logBatch}
          className="focus-ring h-10 shrink-0 rounded-control bg-bartlettGold px-3 font-mono text-[11px] font-semibold text-ink disabled:cursor-not-allowed disabled:opacity-40"
        >
          {batch.isPending
            ? 'Logging…'
            : selectedTouches > 0
              ? `Log all · ${selectedTouches}`
              : 'Select touches'}
        </button>
        <button
          type="button"
          aria-label="Clear selected touches"
          title="Clear selected touches"
          disabled={selectedTouches === 0 || batch.isPending}
          onClick={() => setSelections({})}
          className="focus-ring inline-flex h-10 w-9 shrink-0 items-center justify-center rounded-control border border-white/20 font-mono text-sm text-white disabled:opacity-30"
        >
          ×
        </button>
      </aside>
    </div>
  );
}

function TrackingRow({
  noun,
  atLimit,
  row,
  highlighted,
  selected,
  onToggle,
}: {
  noun: string;
  atLimit: boolean;
  row: ProspectTrackingRow;
  highlighted: boolean;
  selected: ProspectTouchType[];
  onToggle: (type: ProspectTouchType) => void;
}) {
  const patch = usePatchProspectProfile();
  const profile = row.profile;
  const playerLink = profile.playerId ?? profile.linkedPlayerId;

  return (
    <article
      className={[
        'grid min-h-[68px] grid-cols-[130px_minmax(270px,1fr)_100px_minmax(510px,1.5fr)_52px] items-center gap-3 px-3 py-2 text-sm transition-colors',
        highlighted ? 'bg-bartlettGold/10' : 'bg-white',
      ].join(' ')}
    >
      <div className="flex items-center gap-1">
        <button
          type="button"
          title="Move up"
          aria-label={`Move ${profile.displayName} up one priority`}
          onClick={() => {
            void patch.mutateAsync({
              id: profile.id,
              patch: { priorityGroup: movePriority(profile.priorityGroup, 'up') },
            });
          }}
          className="focus-ring inline-flex h-8 w-7 items-center justify-center rounded-control border border-border bg-white font-mono text-xs font-semibold hover:border-bartlett/40"
        >
          ↑
        </button>
        <select
          aria-label={`${profile.displayName} priority`}
          value={profile.priorityGroup == null ? '' : String(profile.priorityGroup)}
          onChange={(event) => {
            const next = event.target.value === '' ? null : Number.parseInt(event.target.value, 10);
            void patch.mutateAsync({ id: profile.id, patch: { priorityGroup: next } });
          }}
          className="focus-ring h-8 min-w-0 flex-1 rounded-control border border-border bg-white px-2 font-mono text-[12px] font-semibold text-ink"
        >
          <option value="">—</option>
          <option value="0">P0</option>
          <option value="1">P1</option>
          <option value="2">P2</option>
          <option value="3">P3</option>
        </select>
        <button
          type="button"
          title="Move down"
          aria-label={`Move ${profile.displayName} down one priority`}
          onClick={() => {
            void patch.mutateAsync({
              id: profile.id,
              patch: { priorityGroup: movePriority(profile.priorityGroup, 'down') },
            });
          }}
          className="focus-ring inline-flex h-8 w-7 items-center justify-center rounded-control border border-border bg-white font-mono text-xs font-semibold hover:border-bartlett/40"
        >
          ↓
        </button>
      </div>

      <div className="flex min-w-0 items-center gap-2.5">
        <PlayerAvatar
          firstName={profile.firstName}
          lastName={profile.lastName}
          displayName={profile.displayName}
          nhlId={profile.nhlId}
          imageUrl={profile.headshotUrl ?? profile.heroImageUrl}
          mediaChecked={Boolean(profile.mediaCheckedAt)}
          className="h-10 w-10 rounded-full"
        />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-baseline gap-2">
            {playerLink ? (
              <Link
                to={`/people/${playerLink}`}
                className="truncate font-semibold text-ink hover:text-bartlett"
              >
                {profile.displayName}
              </Link>
            ) : (
              <span className="truncate font-semibold text-ink">{profile.displayName}</span>
            )}
            <span className="shrink-0 font-mono text-[10px] text-muted">{profile.team ?? '—'}</span>
          </div>
          <ProspectProgress row={row} />
        </div>
      </div>

      <div className="font-mono text-[11px] text-muted">{row.latestTouchAt ?? '—'}</div>
      <InlineTouchSelector atLimit={atLimit} profile={profile} selected={selected} onToggle={onToggle} />
      <button
        type="button"
        title={`Move off ${noun} list`}
        aria-label={`Archive ${profile.displayName}`}
        onClick={() => {
          if (window.confirm(`Archive ${profile.displayName} from the active ${noun} list?`)) {
            void patch.mutateAsync({
              id: profile.id,
              patch: { status: 'archived', notes: appendArchivedNote(profile.notes) },
            });
          }
        }}
        className="inline-flex h-8 w-8 items-center justify-center rounded-control border border-border bg-white font-mono text-[13px] text-muted hover:border-flag-warn/50 hover:text-flag-warn"
      >
        ×
      </button>
    </article>
  );
}

function InlineTouchSelector({
  atLimit,
  profile,
  selected,
  onToggle,
}: {
  atLimit: boolean;
  profile: ProspectProfile;
  selected: ProspectTouchType[];
  onToggle: (type: ProspectTouchType) => void;
}) {
  return (
    <div className="flex min-w-0 items-center gap-1">
      {PROSPECT_TOUCH_TYPES.map((touch) => {
        const active = selected.includes(touch.key);
        return (
          <button
            key={touch.key}
            type="button"
            title={`${touch.label} (+${touch.points})`}
            aria-pressed={active}
            aria-label={`${profile.displayName}: ${touch.label} (+${touch.points})`}
            disabled={atLimit && !active}
            onClick={() => onToggle(touch.key)}
            className={[
              'focus-ring h-8 shrink-0 whitespace-nowrap rounded-control border px-2 font-mono text-[10px] font-semibold transition-colors',
              active
                ? 'border-bartlett bg-bartlett text-white'
                : 'border-border bg-white text-ink hover:border-bartlett/40',
            ].join(' ')}
          >
            {SHORT_TOUCH_LABELS[touch.key]} +{touch.points}
          </button>
        );
      })}
    </div>
  );
}

function PaceGroup({
  title,
  tone,
  rows,
}: {
  title: string;
  tone: 'good' | 'neutral' | 'warn';
  rows: ProspectTrackingRow[];
}) {
  const toneClass =
    tone === 'good' ? 'text-flag-ok' : tone === 'warn' ? 'text-flag-warn' : 'text-bartlett';
  return (
    <div className="surface-soft overflow-hidden rounded-control">
      <div className="flex items-baseline justify-between border-b border-border px-3 py-2">
        <h2 className={['text-sm font-semibold', toneClass].join(' ')}>{title}</h2>
        <span className="font-mono text-[11px] text-muted">{rows.length}</span>
      </div>
      <div className="max-h-52 divide-y divide-border overflow-auto bg-white">
        {rows.length === 0 ? (
          <p className="p-3 text-xs text-muted">No players in this group.</p>
        ) : (
          rows.slice(0, 30).map((row) => (
            <div key={row.profile.id} className="px-3 py-2">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate font-semibold">
                  {row.priorityLabel} · {row.profile.displayName}
                </span>
                <span className="shrink-0 font-mono text-[10px] text-muted">
                  {row.weightedTouchPoints}/{row.goalPoints}
                </span>
              </div>
              <ProspectProgress row={row} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ProspectProgress({ row }: { row: ProspectTrackingRow }) {
  return (
    <ProspectProgressBar
      earned={row.weightedTouchPoints}
      goal={row.goalPoints}
      progressPercent={row.progressPercent}
      pace={row.pace}
    />
  );
}

type ProspectTrackingRow = {
  profile: ProspectProfile;
  priorityRank: number;
  priorityLabel: string;
  weightedTouchPoints: number;
  goalPoints: number;
  progressPercent: number;
  pace: ProspectPace;
  latestTouchAt: string | null;
};

function buildTrackingRows(
  profiles: ProspectProfile[],
  touches: ProspectTouch[]
): ProspectTrackingRow[] {
  const touchesByProfile = new Map<string, ProspectTouch[]>();
  for (const touch of touches) {
    if (!touch.prospectId) continue;
    touchesByProfile.set(touch.prospectId, [
      ...(touchesByProfile.get(touch.prospectId) ?? []),
      touch,
    ]);
  }

  return profiles.map((profile) => {
    const profileTouches = touchesByProfile.get(profile.id) ?? [];
    const progress = prospectProgress(
      profileTouches.map((touch) => touch.touchType),
      profile.priorityGroup
    );
    return {
      profile,
      priorityRank: profile.priorityGroup ?? 9,
      priorityLabel: priorityLabelFor(profile.priorityGroup),
      weightedTouchPoints: progress.earned,
      goalPoints: progress.goal,
      progressPercent: progress.progressPercent,
      pace: progress.pace,
      latestTouchAt: latestTouchDate(profileTouches),
    };
  });
}

function orderTrackingRows(rows: ProspectTrackingRow[], search: string): ProspectTrackingRow[] {
  return [...rows].sort((a, b) => {
    const aMatch = matchesPlayerSearch(a.profile, search);
    const bMatch = matchesPlayerSearch(b.profile, search);
    if (aMatch !== bMatch) return aMatch ? -1 : 1;
    return (
      a.priorityRank - b.priorityRank ||
      a.profile.lastName.localeCompare(b.profile.lastName) ||
      a.profile.firstName.localeCompare(b.profile.firstName)
    );
  });
}

function matchesPlayerSearch(profile: ProspectProfile, search: string): boolean {
  const term = search.trim().toLowerCase();
  return term.length > 0 && profile.displayName.toLowerCase().includes(term);
}

function latestTouchDate(touches: ProspectTouch[]): string | null {
  return (
    touches
      .map((touch) => touch.occurredAt)
      .filter((value): value is string => Boolean(value))
      .sort((a, b) => b.localeCompare(a))[0] ?? null
  );
}

function priorityLabelFor(priority: number | null): string {
  return priority === 0 || priority === 1 || priority === 2 || priority === 3
    ? `P${priority}`
    : '—';
}

function movePriority(priority: number | null, direction: 'up' | 'down'): number {
  const current = priority ?? 3;
  return direction === 'up' ? Math.max(0, current - 1) : Math.min(3, current + 1);
}

function appendArchivedNote(notes: string | null): string {
  const marker = `Archived from touch tracker ${new Date().toISOString().slice(0, 10)}`;
  return notes ? `${notes}\n${marker}` : marker;
}

function Metric({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Target;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-control border border-border bg-bg/70 px-3 py-2">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wide text-muted">
        <Icon size={13} />
        {label}
      </div>
      <div className="mt-1 font-mono text-lg font-semibold text-ink">{value}</div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="space-y-3">
      <div className="grid gap-3 lg:grid-cols-3">
        {[0, 1, 2].map((item) => (
          <div key={item} className="h-44 animate-pulse rounded-control bg-border/30" />
        ))}
      </div>
      <div className="h-80 animate-pulse rounded-control bg-border/30" />
    </div>
  );
}

function skippedMessage(skipped: SkippedTouch[]): string {
  const list = skipped.map((item) => `${item.displayName} (${SHORT_TOUCH_LABELS[item.touchType as ProspectTouchType] ?? item.touchType})`);
  return `Skipped ${skipped.length} already logged for that day: ${list.join(', ')}.`;
}

function batchErrorMessage(error: unknown): string {
  const body = error instanceof ApiError ? (error.body as { error?: string; skipped?: SkippedTouch[] } | null) : null;
  if (body?.error === 'batch_already_logged') return 'This batch was already saved.';
  if (body?.error === 'touch_already_logged' && body.skipped?.length) return `Nothing new to log. ${skippedMessage(body.skipped)}`;
  return 'Batch not logged. Check your connection and try again.';
}
